import React, { useMemo } from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from "react-i18next";

import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetAlert, { WidgetErrorType } from 'components/widgetParts/WidgetAlert';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import withQueryWidget, { WithQueryWidgetProps } from 'components/widgetParts/queryWidgetComposition/withQueryWidget';
import { getWidgetResponsesByName, widgetQueryTitleByResponses, WidgetResponseItem } from 'system/widget/Widget';
import { defaultSettings } from './BubbleComparisonSettings';
import { Query } from 'system/query/Query';
import { BubbleChart, BubbleChartProps, BubbleDataPoint } from 'components/charting/BubbleChart';

function BubbleComparisonContent(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();

  const [drilldownTitle, setDrilldownTitle] = React.useState<string | null>(null);
  const [drilldownQuery, setDrilldownQuery] = React.useState<Query | null>(null);
  const [drilldownQueryIds, setDrilldownQueryIds] = React.useState<string[]>([]);

  const currentState = {...defaultSettings, ...props.widget?.data?.settings};

  function findDataValue(data: any, key: string): number {
    if (!data || !data._total || !Object.hasOwnProperty.call(data, key)) {
      return data?._total || 0;
    }
    const row = data[key];
    if (typeof(row) === 'number') {
      return row;
    } else if (typeof(row) === 'object' && row && row?._buckets && Object.hasOwnProperty.call(row, '_buckets')) {
      return row._buckets?.[0]?._count;
    }
    return data._total;
  }

  function makeDataPoint(responseItem: WidgetResponseItem, valueOptions: { xAsPercent: boolean; yAsPercent: boolean; sizeAsPercent: boolean } ): BubbleDataPoint | undefined {
    const data = responseItem.responseBody;
    if (!data || !data._total) {
      return undefined;
    }

    const info = {
      x: findDataValue(data, 'x') || 0,
      y: findDataValue(data, 'y') || 0,
      size: findDataValue(data, 'size') || 0,
      name: responseItem.queryDisplayTitle,
      color: responseItem.originatingRequest?.queryRequestColor || theme.palette.primary.main
    };

    if (valueOptions.xAsPercent) {
      info.x = info.x / data._total * 100;
    }
    if (valueOptions.yAsPercent) {
      info.y = info.y / data._total * 100;
    }
    if (valueOptions.sizeAsPercent) {
      info.size = info.size / data._total * 100;
    }

    return info;
  }

  function labelFromAgg(agg: any): string {

    let label = i18n.t('records');
    if (!agg) {
      return label;
    }

    if (agg.label) {
      label = agg.label;
    } else if (agg.op === 'social_universe') {
      label = i18n.t('social_universe');
    } else if (agg.field) {
      label = agg.field;
      if (agg.filter && Array.isArray(agg.filter) && agg.filter.length > 0) {
        label += ` - ${agg.filter.join(', ')}`;
      }
    }

    return label;
  }

  const bubbleChartInfo: BubbleChartProps = useMemo(() => {
    const info: BubbleChartProps = {
      sizeLabel: ' ',
      xLabel: ' ',
      yLabel: ' ',
      dataPoints: []
    };

    const apiResponses = getWidgetResponsesByName(props.apiResponses, "bubble-comparison");

    const firstResponse = apiResponses?.[0];
    const request = firstResponse?.originatingRequest?.originalRequest;
    const aggs = request?.requestInit?.body?.aggs;

    if (!aggs) {
      return info;
    }

    info.xAsPercent = currentState.xAxisKeywordAsPercent && aggs.x?.op === 'keyword';
    info.yAsPercent = currentState.yAxisKeywordAsPercent && aggs.y?.op === 'keyword';
    info.sizeAsPercent = currentState.sizeKeywordAsPercent && aggs.size?.op === 'keyword';

    info.sizeLabel = labelFromAgg(aggs.size);
    info.xLabel = labelFromAgg(aggs.x);
    info.yLabel = labelFromAgg(aggs.y);

    info.dataPoints = apiResponses?.map((responseItem: WidgetResponseItem, index: number) => {
      const activeRequest = responseItem?.originatingRequest;
      const dataPoint = makeDataPoint(responseItem, { xAsPercent: !!info.xAsPercent, yAsPercent: !!info.yAsPercent, sizeAsPercent: !! info.sizeAsPercent });
      if (!dataPoint) {
        return {
          x: 0,
          y: 0,
          size: 0,
          name: responseItem.queryDisplayTitle,
          color: activeRequest.queryRequestColor || theme.colors.namedColors.chartColorArray[index % theme.colors.namedColors.chartColorArray.length]
        };
      }
      dataPoint.color = activeRequest.queryRequestColor || theme.colors.namedColors.chartColorArray[index % theme.colors.namedColors.chartColorArray.length];
      return dataPoint;
    }).filter(dataPoint => dataPoint) as BubbleDataPoint[] || [];

    return info;
  }, [props.apiResponses, props.widget, i18n]);

  const widgetQueryTitle = widgetQueryTitleByResponses(props.apiResponses, "bubble-comparison", i18n);

  return (
    <>
      {bubbleChartInfo?.dataPoints?.length !== 0 &&
        <WidgetLayout
          header={
            <WidgetTitle
              alignment={props.widget?.data?.settings?.widgetTitleAlignment}
              customTitle={props.widget?.data?.settings?.customWidgetTitle}
              defaultTitle={i18n.t('widget-types.bubble-comparison.label')}
              settingsTitle={props.widget?.data?.settings?.widgetTitle}
              widgetQueryTitle={widgetQueryTitle}
              />
          }
        >
          <BubbleChart
            {...bubbleChartInfo}
          />
        </WidgetLayout>
      }
      {bubbleChartInfo?.dataPoints?.length === 0 &&
        <WidgetAlert type={WidgetErrorType.NO_DATA} />
      }
    </>
  );
}

export default withQueryWidget(BubbleComparisonContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true
});
